import type { DeepString } from "@/lib/i18n";

/**
 * CUserAccounts — polskie tłumaczenia (login/register/remind/reset hasła).
 *
 * Backend `content.lang.*` (z `c_user_accounts_{lang}.lang`) jest preferowany
 * dla labelek/przycisków — tutaj tylko fallbacki + zod messages + error
 * messages systemowe (network, server-side fail bez `errors`).
 */
export const pl = {
  empty: "Brak danych do wyświetlenia.",
  registerDisabled: "Rejestracja jest wyłączona.",

  /* ─── Logout ─── */
  logout: {
    failed: "Nie udało się wylogować.",
  },

  /* ─── Akcje (fallbacki backend lang) ─── */
  actions: {
    login: "Zaloguj się",
    register: "Załóż konto",
    remindPass: "Zapomniałeś hasła?",
    backToLogin: "Wróć do logowania",
    logout: "Wyloguj",
  },

  /* ─── Labelki pól (fallbacki backend lang) ─── */
  labels: {
    login: "Login lub e-mail",
    pass: "Hasło",
    rememberMe: "Zapamiętaj mnie na 7 dni",
    email: "E-mail",
    userGroup: "Grupa użytkownika",
    name: "Imię",
    surname: "Nazwisko",
    telephone: "Telefon",
    passwordRegister: "Hasło",
    ppass: "Powtórz hasło",
    regulamin: "Akceptuję regulamin",
    regulaminLink: "zobacz",
    personalData: "Wyrażam zgodę na przetwarzanie moich danych osobowych.",
    marketing: "Zgoda na komunikację marketingową",
    emailOrLogin: "Email lub login",
  },

  /* ─── Zod validation messages ─── */
  validation: {
    loginRequired: "Podaj login lub adres email.",
    passwordRequired: "Podaj hasło.",
    emailRequired: "Pole email jest wymagane.",
    invalidEmail: "Niepoprawny adres email.",
    passwordRegRequired: "Hasło jest wymagane.",
    passwordRepeatRequired: "Powtórz hasło.",
    passwordMismatch: "Hasła nie zgadzają się.",
    passwordRequirements:
      "Hasło nie spełnia wymagań — sprawdź listę pod polem.",
    requiredRegulamin: "Akceptacja regulaminu jest wymagana.",
    requiredPersonalData: "Wymagana zgoda na przetwarzanie danych osobowych.",
    emailOrLoginRequired: "Podaj email lub login.",
    newPassRequired: "Podaj nowe hasło.",
    newPpassRequired: "Powtórz nowe hasło.",
  },

  /* ─── Password strength meter ─── */
  passwordStrength: {
    strengthLabel: "Siła hasła",
    levels: {
      veryWeak: "Bardzo słabe",
      weak: "Słabe",
      fair: "Średnie",
      good: "Mocne",
    },
    requirements: {
      /* Placeholders {min}/{max} podstawiane przez buildPasswordStrengthLabels
         z payloadu backendu (`password_requirements.min_length/max_length`). */
      length: "{min}–{max} znaków",
      lowercase: "Mała litera (a–z)",
      uppercase: "Wielka litera (A–Z)",
      digit: "Cyfra (0–9)",
      special: "Znak specjalny (!@#$…)",
    },
  },

  /* ─── Login form statusy ─── */
  login: {
    failed: "Logowanie nie powiodło się.",
  },

  /* ─── Register form ─── */
  register: {
    failed: "Rejestracja nie powiodła się.",
    successDefault: "Konto utworzone. Sprawdź email aby aktywować.",
    /* Placeholder {email} podstawiany przez RegisterForm.onSubmit z wartości
       formularza (values.email). Email wyróżniony jako `<strong>` przez split
       w komponencie (bez HTML w lang — czysty JSX). Info o SPAM-ie ważne bo
       filtry (Gmail Promotions, Outlook Junk) potrafią schować aktywacyjny mail. */
    successWithEmail:
      "Rejestracja pomyślna! Wiadomość z linkiem aktywacyjnym została wysłana na adres {email}. Sprawdź skrzynkę odbiorczą oraz folder SPAM (wiadomości niechciane) — wiadomość może trafić tam zamiast do głównego folderu.",
    /* Pokazywane gdy backend zwraca `mail_sent: false` — konto zostało
       utworzone (sukces walidacji), ale SMTP failuje. User musi
       zareagować inaczej niż czekać na email (skontaktować się z adminem). */
    successMailFailed:
      "Konto zostało utworzone, ale nie udało się wysłać wiadomości z linkiem aktywacyjnym na adres {email}. Skontaktuj się z administratorem serwisu lub spróbuj ponownie poprosić o weryfikację konta po zalogowaniu.",
    recaptchaHint:
      "Backend wymaga reCAPTCHA — komponent reCAPTCHA dla Imperial CMS będzie dodany w kolejnej iteracji. Tymczasem walidacja po stronie backendu jest tolerancyjna (test mode).",
  },

  /* ─── Remind password ─── */
  remind: {
    title: "Zapomniałem hasła",
    hint: "Wpisz email lub login. Wyślemy link do resetu hasła.",
    submit: "Wyślij link",
    failed: "Nie udało się wysłać linku resetu.",
    successDefault: "Jeśli konto istnieje, link do resetu hasła został wysłany na email.",
  },

  /* ─── Reset password ─── */
  reset: {
    title: "Ustaw nowe hasło",
    newPass: "Nowe hasło",
    repeatPass: "Powtórz nowe hasło",
    submit: "Zmień hasło",
    invalidLink: "Nieprawidłowy link resetu hasła. Spróbuj wysłać nowy link.",
    failed: "Nie udało się zmienić hasła.",
    successDefault: "Hasło zostało zmienione. Możesz się teraz zalogować.",
  },

  /* ─── Aktywacja konta założonego bez hasła (link z maila rejestracyjnego) ───
     Widoczne tylko przez moment - po aktywacji landing przenosi do formularza hasła. */
  verifySetPassword: {
    title: "Aktywacja konta",
    pending: "Aktywujemy Twoje konto...",
    invalidLink: "Nieprawidłowy link aktywacyjny. Skorzystaj z opcji przypomnienia hasła.",
    failed:
      "Nie udało się aktywować konta. Link mógł wygasnąć lub zostać już użyty - skorzystaj z opcji przypomnienia hasła.",
  },

  /* OAuth (FB/Google) translations — przeniesione do shared OAuthButtons.i18n
     (komponent ma własne tłumaczenia bo jest używany też w WUserAccount). */

  /* ─── Profile tabs (ProfileTabs.tsx) ─── */
  profile: {
    loading: "Ładowanie danych profilu…",
    loadFailed: "Nie udało się pobrać danych profilu.",
    expandMenu: "Rozwiń menu",
    collapseMenu: "Zwiń menu",
    tabs: {
      personal: "Twoje dane",
      password: "Zmiana hasła",
      account: "Konto",
      users: "Użytkownicy",
      security: "Bezpieczeństwo",
    },
  },

  /* ─── Karta Bezpieczeństwo (MFA / 2FA) - ProfileTabSecurity.tsx ─── */
  security: {
    title: "Uwierzytelnianie dwuskładnikowe",
    loading: "Ładowanie stanu zabezpieczeń…",
    loadFailed: "Nie udało się pobrać stanu zabezpieczeń.",

    /* Setup panel (MFA wyłączone) */
    setupIntro: "Uwierzytelnianie dwuskładnikowe (2FA) dodaje dodatkową warstwę ochrony do Twojego konta. Przy logowaniu będziesz podawać 6-cyfrowy kod z aplikacji na telefonie oprócz hasła.",
    actionSetupStart: "Wygeneruj klucz",
    setupFailed: "Nie udało się rozpocząć konfiguracji. Spróbuj ponownie.",

    step1Title: "Zeskanuj kod QR",
    step1Description: "Otwórz aplikację uwierzytelniającą (Google Authenticator, Microsoft Authenticator) i zeskanuj poniższy kod QR.",
    step2Title: "Wpisz kod z aplikacji",
    step2Description: "Aplikacja wygeneruje 6-cyfrowy kod, który zmienia się co 30 sekund. Wprowadź go poniżej.",

    qrAlt: "Kod QR do aplikacji uwierzytelniającej",
    qrManualEntry: "Nie możesz zeskanować? Wprowadź ten klucz ręcznie:",

    actionEnable: "Włącz uwierzytelnianie",
    enableFailed: "Nie udało się włączyć uwierzytelniania. Spróbuj ponownie.",
    invalidCode: "Nieprawidłowy kod. Sprawdź kod w aplikacji i spróbuj ponownie.",
    rateLimitExceeded: "Zbyt wiele nieudanych prób. Zainicjuj konfigurację ponownie, aby wygenerować nowy klucz.",

    /* Disable panel (MFA włączone) */
    enabledTitle: "Uwierzytelnianie dwuskładnikowe jest aktywne",
    enabledDescription: "Twoje konto jest chronione dodatkową warstwą zabezpieczeń. Przy logowaniu wymagany jest kod z aplikacji uwierzytelniającej.",
    disablePasswordLabel: "Aktualne hasło",
    actionDisable: "Wyłącz uwierzytelnianie",
    disableFailed: "Nie udało się wyłączyć uwierzytelniania.",
    invalidPassword: "Nieprawidłowe hasło. Sprawdź pisownię i spróbuj ponownie.",
    disableSuccess: "Uwierzytelnianie dwuskładnikowe zostało wyłączone.",
    /* Info banner pokazywany gdy MFA jest skonfigurowane i grupa userska ma
       `2fa_option = 'required'` - user nie moze wylaczyc, wiec disable form
       jest ukryty, a on widzi tylko status + info dlaczego. Analog PHP
       admin `tfa_required_by_group_configured`. */
    requiredByGroupConfigured: "Twoja grupa użytkowników wymaga aktywnego uwierzytelniania dwuskładnikowego - wyłączenie nie jest możliwe.",
  },

  /* ─── Bramka MFA (MfaGuard.tsx) - twarda blokada gdy `mfa_option === "required"` ─── */
  mfaGuard: {
    title: "Wymagane uwierzytelnianie dwuskładnikowe",
    description: "Twoja grupa użytkowników wymaga aktywnego uwierzytelniania dwuskładnikowego. Skonfiguruj MFA w zakładce Bezpieczeństwo, aby zwiększyć poziom ochrony konta.",
    linkToSecurity: "Przejdź do konfiguracji",
  },

  /* ─── AuthMfaModal (drugi krok logowania z 2FA) ─── */
  authMfaModal: {
    title: "Uwierzytelnianie dwuskładnikowe",
    intro: "Podaj 6-cyfrowy kod z aplikacji uwierzytelniającej (Google Authenticator, Microsoft Authenticator).",
    close: "Zamknij",
    verify: "Zweryfikuj",
    cancel: "Anuluj",
    invalidCode: "Nieprawidłowy kod. Spróbuj ponownie.",
    sessionExpired: "Sesja weryfikacji wygasła.",
    ariaLabel: "Uwierzytelnianie dwuskładnikowe",
  },

  /* ─── Lista użytkowników (ProfileTabUserList.tsx) ─── */
  userList: {
    title: "Użytkownicy",
    loading: "Ładowanie listy użytkowników…",
    loadFailed: "Nie udało się pobrać listy użytkowników.",
    empty: "Brak użytkowników do wyświetlenia.",
    searchPlaceholder: "Szukaj...",
    columnUser: "Użytkownik",
    columnEmail: "E-mail",
    columnPhone: "Telefon",
    columnAction: "Akcja",
    sendMessage: "Wyślij wiadomość",
    /* Tekst gdy komponent wiadomości prywatnych (c_private_messages) niedostępny. */
    messagesUnavailable: "niedostępne",
    noEmail: "brak",
    noPhone: "brak",
    truncatedHint: "Lista została ograniczona do pierwszych pozycji.",
    /* Paginacja gridu (client-side). */
    paginationPrev: "Poprzednia",
    paginationNext: "Następna",
    paginationInfo: "Strona {page} z {pages}",
  },
} as const;

export type CUserAccountsTranslations = DeepString<typeof pl>;
